import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Loader2 } from 'lucide-react';

const DOT = { green: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-red-500' };
const ICON = { green: CheckCircle2, amber: AlertTriangle, red: XCircle };
const TEXT = { green: 'text-emerald-700', amber: 'text-amber-700', red: 'text-red-700' };

function StatusRow({ label, status, details, testId }) {
  const Icon = ICON[status] || AlertTriangle;
  return (
    <div className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0" data-testid={testId}>
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${TEXT[status] || 'text-gray-400'}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800">{label}</p>
        <p className="text-xs text-gray-500">{details}</p>
      </div>
    </div>
  );
}

const PENDING_LABELS = {
  duplicate_numbers: 'Duplicate numbers',
  duplicate_jobs: 'Duplicate jobs',
  contact_states: 'Contact states',
  job_discounts: 'Job discounts',
  commissions: 'Commissions',
  orphaned_users: 'Orphaned users',
  converted_leads_incomplete: 'Incomplete leads',
};

export default function GoLiveCheckCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/maintenance/go-live-check');
      setData(res.data);
    } catch { /* status stays as-is */ }
    finally { setLoading(false); }
  };

  useEffect(() => { run(); }, []);

  const currentDomain = typeof window !== 'undefined' ? window.location.origin : '';
  const domainMatches = data?.public_base_url && data.public_base_url.replace(/\/$/, '') === currentDomain.replace(/\/$/, '');
  const overallLabel = { green: 'Ready to operate', amber: 'Review before operating', red: 'Do NOT operate yet' };

  return (
    <Card className="bg-white border-gray-100" data-testid="go-live-check-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${data ? DOT[data.overall] : 'bg-gray-300'}`} data-testid="go-live-overall-dot" />
            <p className="text-sm font-bold text-gray-800 uppercase tracking-wide">
              Go-Live Check{data && <span className={`ml-2 ${TEXT[data.overall]}`}>— {overallLabel[data.overall]}</span>}
            </p>
          </div>
          <Button data-testid="go-live-run-check-btn" size="sm" variant="outline" onClick={run} disabled={loading} className="border-[#003366]/20 text-[#003366]">
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Run Check
          </Button>
        </div>

        {data && (
          <>
            <div className="grid sm:grid-cols-2 gap-x-6">
              <StatusRow testId="go-live-email-row" label="Email (Resend)" status={data.email.status}
                details={!data.email.configured ? 'Not configured' : data.email.test_mode ? `TEST MODE — redirected to ${data.email.test_to || '(no test_to set)'}` : `Live — sending from ${data.email.from}`} />
              <StatusRow testId="go-live-sms-row" label="SMS (Twilio)" status={data.sms.status}
                details={!data.sms.configured ? 'Not configured' : data.sms.test_mode ? 'TEST MODE — redirected to test number' : `Live — sending from ${data.sms.from}`} />
              <StatusRow testId="go-live-stripe-row" label="Stripe" status={data.stripe.status}
                details={!data.stripe.configured ? 'Not configured' : `${data.stripe.mode.toUpperCase()} key · webhook ${data.stripe.webhook_configured ? 'configured' : 'MISSING'}`} />
              <StatusRow testId="go-live-twilio-row" label="Twilio (voice)" status={data.twilio.status}
                details={!data.twilio.configured ? 'Missing variables' : `Signature validation ${data.twilio.signature_validation ? 'ON' : 'OFF'}${data.twilio.auth_token_warning ? ' · ' + data.twilio.auth_token_warning : ''}`} />
            </div>

            {data.inconsistent_test_modes && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="go-live-inconsistent-warning">
                Email and SMS test modes don't match — a customer could get the SMS about an estimate but never the email with it.
              </div>
            )}

            <div className="text-xs text-gray-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="go-live-public-base-url">
              <span className="font-semibold">PUBLIC_BASE_URL:</span> {data.public_base_url || '—'}{' '}
              {domainMatches ? <span className="text-emerald-600">(matches this domain)</span> : <span className="text-amber-600">(different from the domain you're viewing this from)</span>}
            </div>

            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Maintenance pending</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                {Object.entries(data.maintenance_pending).map(([key, count]) => (
                  <div key={key} className={`rounded-lg border p-2 ${count > 0 ? 'border-amber-200 bg-amber-50' : 'border-emerald-100 bg-emerald-50/50'}`}>
                    <p className={`text-lg font-bold ${count > 0 ? 'text-amber-700' : 'text-emerald-600'}`}>{count}</p>
                    <p className="text-[10px] text-gray-500">{PENDING_LABELS[key] || key}</p>
                  </div>
                ))}
                <div className={`rounded-lg border p-2 ${data.broken_payment_links > 0 ? 'border-red-200 bg-red-50' : 'border-emerald-100 bg-emerald-50/50'}`}>
                  <p className={`text-lg font-bold ${data.broken_payment_links > 0 ? 'text-red-700' : 'text-emerald-600'}`}>{data.broken_payment_links}</p>
                  <p className="text-[10px] text-gray-500">Broken payment links</p>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
